import { Add } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, DownloadFileNameLink, GreenText, LabelField, Title } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getNeedsToBeMovedToStagingToDoList } from 'controllers/toDoLists/outboundTrips/index'
import { VehicleDto } from 'dtos'
import { NeedsToBeMovedToStagingDto, NeedsToBeMovedToStagingQueryParams } from 'dtos/toDoLists/outboundTrips'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateMoment, useDebounce } from 'utils'

export default function NeedsToBeMovedToStagingToDoList() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<NeedsToBeMovedToStagingQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'readyForOutboundDate' })
  const [toDoList, setToDoList] = useState<NeedsToBeMovedToStagingDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: NeedsToBeMovedToStagingQueryParams) => {
    layout.setIsLayoutLoading(true)
    getNeedsToBeMovedToStagingToDoList(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Title>Needs To Be Moved To Staging</Title>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='submittedToDOT'>Submitted To DOT</MenuItem>

                    <MenuItem value='readyForOutboundDate'>Ready For Outbound Date</MenuItem>

                    <MenuItem value='vehicleReleaseDate'>Vehicle Release Date</MenuItem>

                    <MenuItem value='bondReleaseDate'>Bond Release Date</MenuItem>

                    <MenuItem value='outboundTripNumber'>Outbound Trip #</MenuItem>

                    <MenuItem value='outboundTripDate'>Outbound Trip Date</MenuItem>

                    <MenuItem value='buyerExporterCompany'>Exporter/Buyer</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'outboundTripNumber', 'buyerExporterCompany', 'importerStockNumber', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['submittedToDOT', 'readyForOutboundDate', 'vehicleReleaseDate', 'bondReleaseDate', 'outboundTripDate'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (['submittedToDOT', 'readyForOutboundDate', 'vehicleReleaseDate', 'bondReleaseDate', 'outboundTripDate'].includes(filterField)
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'submittedToDOT') {
                        setQueryParams({
                          ...queryParams,
                          sentToDOTDateTimeFrom: filterValueFrom,
                          sentToDOTDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'readyForOutboundDate') {
                        setQueryParams({
                          ...queryParams,
                          readyForOutboundDateTimeFrom: filterValueFrom,
                          readyForOutboundDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleReleaseDate') {
                        setQueryParams({
                          ...queryParams,
                          vehicleReleaseDateTimeFrom: filterValueFrom,
                          vehicleReleaseDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'bondReleaseDate') {
                        setQueryParams({
                          ...queryParams,
                          bondReleaseDateTimeFrom: filterValueFrom,
                          bondReleaseDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'outboundTripDate') {
                        setQueryParams({
                          ...queryParams,
                          outboundTripDateFrom: filterValueFrom,
                          outboundTripDateTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.readyForOutboundDateTimeFrom ||
                          queryParams.readyForOutboundDateTimeTo ||
                          queryParams.vehicleReleaseDateTimeFrom ||
                          queryParams.vehicleReleaseDateTimeTo ||
                          queryParams.bondReleaseDateTimeFrom ||
                          queryParams.bondReleaseDateTimeTo ||
                          queryParams.sentToDOTDateTimeFrom ||
                          queryParams.sentToDOTDateTimeTo ||
                          queryParams.outboundTripDateFrom ||
                          queryParams.outboundTripDateTo ||
                          queryParams.importerStockNumber ||
                          queryParams.vin ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model ||
                          queryParams.buyerExporterCompany ||
                          queryParams.outboundTripNumber
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        readyForOutboundDateTimeFrom: undefined,
                        readyForOutboundDateTimeTo: undefined,
                        vehicleReleaseDateTimeFrom: undefined,
                        vehicleReleaseDateTimeTo: undefined,
                        bondReleaseDateTimeFrom: undefined,
                        bondReleaseDateTimeTo: undefined,
                        sentToDOTDateTimeFrom: undefined,
                        sentToDOTDateTimeTo: undefined,
                        outboundTripDateFrom: undefined,
                        outboundTripDateTo: undefined,
                        importerStockNumber: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined,
                        buyerExporterCompany: undefined,
                        outboundTripNumber: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.sentToDOTDateTimeFrom && queryParams.sentToDOTDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Submitted To DOT from "${moment(queryParams.sentToDOTDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.sentToDOTDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, sentToDOTDateTimeFrom: undefined, sentToDOTDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.readyForOutboundDateTimeFrom && queryParams.readyForOutboundDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Ready For Outbound Date from "${moment(queryParams.readyForOutboundDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.readyForOutboundDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, readyForOutboundDateTimeFrom: undefined, readyForOutboundDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vehicleReleaseDateTimeFrom && queryParams.vehicleReleaseDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Release Date from "${moment(queryParams.vehicleReleaseDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.vehicleReleaseDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vehicleReleaseDateTimeFrom: undefined, vehicleReleaseDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.bondReleaseDateTimeFrom && queryParams.bondReleaseDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Bond Release Date from "${moment(queryParams.bondReleaseDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.bondReleaseDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, bondReleaseDateTimeFrom: undefined, bondReleaseDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.outboundTripNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Outbound Trip # contains "${queryParams.outboundTripNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, outboundTripNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.outboundTripDateFrom && queryParams.outboundTripDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Outbound Trip Date from "${moment(queryParams.outboundTripDateFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.outboundTripDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, outboundTripDateFrom: undefined, outboundTripDateTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.buyerExporterCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter/Buyer contains "${queryParams.buyerExporterCompany}"`}
                    onDelete={() => setQueryParams({ ...queryParams, buyerExporterCompany: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${queryParams.vin}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            {toDoList.map(item => (
              <Grid item xs={12} key={item.vehicleGuid}>
                <Card variant='outlined'>
                  <CardContent>
                    <Grid container spacing={1} alignItems='center'>
                      <Grid item xs={12} sm={3}>
                        <LabelField label='Importer Stock #' value={item.importerStockNumber} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='VIN' value={item.vin} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Year' value={item.year} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Make' value={item.make} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Model' value={item.model} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Exporter/Buyer' value={item.buyerExporterCompany} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField
                          label='Ready For Outbound Date'
                          value={item.readyForOutboundDateTime ? formatDateMoment(item.readyForOutboundDateTime) : '-'}
                        />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Vehicle Release Date' value={item.vehicleReleaseDateTime ? formatDateMoment(item.vehicleReleaseDateTime) : '-'} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Bond Release Date' value={item.bondReleaseDateTime ? formatDateMoment(item.bondReleaseDateTime) : '-'} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Outbound Trip #' value={item.outboundTripNumber} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <LabelField label='Outbound Trip Date' value={item.outboundTripDate ? formatDateMoment(item.outboundTripDate) : '-'} />
                      </Grid>

                      <Grid item xs={12} container spacing={2} alignItems='center' justifyContent='flex-end'>
                        {item.warrantyCertificateFileName && (
                          <Grid item>
                            <DownloadFileNameLink fileName={item.warrantyCertificateFileName} label='Download Warranty Certificate' />
                          </Grid>
                        )}

                        <Grid item>
                          <Button onClick={() => navigate(`/needs-to-be-moved-to-staging/${encodeURIComponent(item.vehicleGuid)}`)} variant='contained'>
                            SCAN BARCODE
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            <Grid item xs={12}>
              <TableContainer>
                <Table>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        SelectProps={{ fullWidth: false }}
                        count={totalCount}
                        rowsPerPage={queryParams.pageSize}
                        page={queryParams.page}
                        onPageChange={(_, page) => {
                          setQueryParams({
                            ...queryParams,
                            page
                          })
                        }}
                        onRowsPerPageChange={e => {
                          setQueryParams({
                            ...queryParams,
                            page: 0,
                            pageSize: e.target.value as unknown as number
                          })
                        }}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Needs To Be Moved To Staging To-Do List:**

Vehicles appear in this list if:
   - The vehicle is associated with an Outbound Trip that is locked.
   - The Outbound Trip Date is today, tomorrow, or in the past.
   - The vehicle does not have a “Warranty Certificate In Glovebox” photo uploaded.

This list is sorted by Outbound Trip Date, from oldest to newest.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Submitted To DOT,
   - Ready For Outbound Date,
   - Vehicle Release Date,
   - Bond Release Date,
   - Outbound Trip #,
   - Outbound Trip Date,
   - Exporter.
   - Buyer,
   - Importer Stock #,
   - VIN,
   - Year,
   - Make,
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Submitted To DOT: Displays Filter Date From and Filter Date To.
   - Ready For Outbound Date: Displays Filter Date From and Filter Date To.
   - Vehicle Release Date: Displays Filter Date From and Filter Date To.
   - Bond Release Date: Displays Filter Date From and Filter Date To.
   - Outbound Trip #:  Displays a free-type filter field.
   - Outbound Trip Date: Displays Filter Date From and Filter Date To.
   - Exporter: Displays a free-type filter field.
   - Buyer: Displays a free-type filter field.
   - Importer Stock # a free-type filter field.
   - VIN: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Importer Stock # Label:**

Displays the importer’s stock number assigned to the vehicle.

**VIN Label:**

Displays the Vehicle Identification Number.

**Year Label:**

Displays the vehicle’s model year.

**Make Label:**

Displays the manufacturer of the vehicle

**Model Label:**

Displays the model name of the vehicle

**Exporter/Buyer Label:**

Displays the Buyer company name if one is associated with the vehicle. If no Buyer is assigned, the Exporter company name is shown instead.

**Ready For Outbound Date Label:**

Read from the Conformity Details card under Vehicle Details – Conformity (tab). This date allows vehicles to be added to Outbound Trips before the Vehicle Release Date is reached (the 30-day DOT hold expires).

**Vehicle Release Date Label:**

Read from the Conformity Details card under Vehicle Details – Conformity (tab). This is the date that the DOT-mandated vehicle hold expires (typically 30 days after submission).

**Bond Release Date Label:**

Read from the Outbound Trip card under Vehicle Details – Conformity (tab). This field is nullable and may not be present for all vehicles. If the Bond Release Date is a past or current date, the vehicle can immediately be added to an Outbound Trip, regardless of the Ready For Outbound Date or Vehicle Release Date.

**Outbound Trip # Label:**

Displays the unique identifier of the Outbound Trip to which the vehicle is assigned.

**Outbound Trip Date Label:**

Displays the scheduled date of the Outbound Trip.

**Download Warranty Certificate Link:**

Clicking this link downloads the vehicles Warranty Certificate PDF to the users device.

**Scan Barcode Button:**

Clicking this button opens the “Needs To Be Moved To Staging – Scan Barcode” screen.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
